import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { AutoTagProcessService } from './auto-tag-process.service';
import * as _ from 'lodash-es';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';

@Component({
    selector: 'gtw-auto-tag-process',
    templateUrl: './auto-tag-process.component.html',
    styleUrls: ['./auto-tag-process.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AutoTagProcessComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private isInitialized = false;
  private isLoading = false;

  filterParams: any = {};
  baseURLs: any = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  rowData: any = {};

  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};

  filteredTaxYearData: any[] = [];
  taxYearData: any[] = [];
  selectedTaxYear: string = '';
  TaxYearKey: string = '';
  curentyear: string = '';
  scenario: string = '';
  jcd_key: string = '';

  scenarioDropDown: any[] = [];
  selectedScenario: string = '';
  showscenario: boolean = false;

  trans_types: any[] = [];
  showTransTypes: boolean = false;

  filingEnityDetails: any[] = [];
  parentDetails: any[] = [];
  dropdownLists: { [k: string]: any[] } = { parentME: [], filerLE: [] };
  parentME: any[] = [];
  filerEntity: any[] = [];
  filingGroup: string = '';
  filingGroupDropDown: any[] = [];

  is_locked: any = null;

  loading: boolean = false;
  loadingMessage: string = 'Loading...';

  savingGroupKey: string | null = null;

  showConfirmModal: boolean = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Initiate Auto Tag Creation Process',
    message: 'Are you sure to initiate the Auto Tag Creation?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private gtwSnackbarService: GtwSnackbarService,
    private autoTagProcessService: AutoTagProcessService,
    private jsonObjectFactory: JsonObjectFactoryService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }
    if (!this.globalService || !this.GENERAL_CONFIG || Object.keys(this.GENERAL_CONFIG).length === 0) {
      return;
    }
    this.isInitialized = true;
    this.init();
  }

  init(): void {
    try {
      this.curentyear = this.globalService.tax_year || '';
      this.scenario = this.globalService.scenario || '';
      this.jcd_key = this.globalService.jcd_key || '';

      this.checkLockStatus();
      this.getTaxYearDropDown();
      this.getParentMeDetails();
    } catch (e) {
      console.error('Error in auto-tag-process init:', e);
    }
  }

  private setLoading(isLoading: boolean, message: string = 'Loading...'): void {
    this.loading = isLoading;
    this.loadingMessage = message;
    this.cdr.detectChanges();
  }

  checkLockStatus(): void {
    const lockparams = {
      action_code: 'n10cpv',
      scenario: this.scenario
    };
    const globalServiceWrapper = { globalParams: this.globalService };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      lockparams,
      globalServiceWrapper,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.is_locked = data.jsonObject;
        if (this.is_locked?.[0]?.LOCK_STATUS === 'Y') {
          this.gtwSnackbarService.openSnackBar(
            `Tax Return has already been filed for tax year ${this.filterParams?.tax_year || this.selectedTaxYear || this.curentyear}.`,
            'gtw-snackbar--red'
          );
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error checking lock status:', error);
        this.cdr.detectChanges();
      }
    );
  }

  getTaxYearDropDown(): void {
    this.setLoading(true, 'Loading Tax Years...');

    const params: any = {
      action_code: 'e83e1x'
    };

    const globalServiceWrapper = { globalParams: this.globalService };

    this.autoTagProcessService.loadJsonObject(
      this.GENERAL_CONFIG.base_url,
      params,
      globalServiceWrapper
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data?.jsonObject) {
          this.taxYearData = _.filter(data.jsonObject, (p: any) =>
            _.includes(['TAX_YEAR'], p.CODE_GROUP));
          this.filteredTaxYearData = [];
          for (const indx in this.taxYearData) {
            if (this.taxYearData[indx].CODE_GROUP === 'TAX_YEAR' &&
              this.taxYearData[indx].CODE_DISP_DESC <= this.curentyear &&
              this.taxYearData[indx].CODE_DISP_DESC >= (parseInt(this.curentyear, 10) - 5)) {
              this.filteredTaxYearData.push(this.taxYearData[indx]);
            }
          }
          this.filteredTaxYearData.sort((a, b) =>
            parseInt(b.CODE_DISP_DESC, 10) - parseInt(a.CODE_DISP_DESC, 10));
        }
        this.cdr.markForCheck();
        this.setLoading(false);
      },
      (error: any) => {
        console.error('Error loading tax year dropdown:', error);
        this.setLoading(false);
      }
    );
  }

  getScenarioDropDown(): void {
    if (!this.selectedTaxYear) {
      this.gtwSnackbarService.openSnackBar('Please select the Tax Year to get the Scenario.', 'gtw-snackbar--red');
      return;
    }

    this.setLoading(true, 'Loading Scenarios...');

    const params = {
      action_code: '7q0wiy',
      tax_year: this.selectedTaxYear
    };

    const globalServiceWrapper = { globalParams: this.globalService };

    this.autoTagProcessService.loadJsonObject(
      this.GENERAL_CONFIG.base_url,
      params,
      globalServiceWrapper
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.scenarioDropDown = data?.jsonObject || [];
        this.scenarioDropDown.sort((a, b) => (a.VALUE || 0) - (b.VALUE || 0));
        this.showscenario = true;
        this.cdr.markForCheck();
        this.setLoading(false);
      },
      (error: any) => {
        console.error('Error loading scenario dropdown:', error);
        this.setLoading(false);
      }
    );
  }

  onScenarioChange(): void {
    this.showTransTypes = false;
    this.getTransType();
    this.getFilingGroup();
  }

  getTransType(): void {
    if (!this.selectedScenario) {
      this.showTransTypes = false;
      return;
    }

    this.setLoading(true, 'Loading Transaction Types...');

    const params = {
      action_code: 'mgl3cv',
      p_scenario: this.selectedScenario,
      p_jcd_key: this.jcd_key
    };

    const globalServiceWrapper = { globalParams: this.globalService };

    this.autoTagProcessService.loadJsonObject(
      this.GENERAL_CONFIG.base_url,
      params,
      globalServiceWrapper
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.trans_types = (data?.jsonObject || []).map((t: any) => ({
          ...t,
          IS_ENABLED: t.IS_ENABLED === 'Y'
        }));
        this.showTransTypes = true;
        this.cdr.markForCheck();
        this.setLoading(false);
      },
      (error: any) => {
        console.error('Error loading trans types:', error);
        this.trans_types = [];
        this.showTransTypes = false;
        this.cdr.markForCheck();
        this.setLoading(false);
      }
    );
  }

  toggleTransJob(t: any): void {
    if (this.savingGroupKey !== null) {
      return;
    }
    t.IS_ENABLED = !t.IS_ENABLED;
    this.updateTransJob(t.GROUP_KEY, t.IS_ENABLED);
  }

  updateTransJob(GROUP_KEY: string, IS_ENABLED: boolean): void {
    this.savingGroupKey = GROUP_KEY;
    this.cdr.detectChanges();

    const params = {
      sso_id: this.USER_SETTINGS?.user?.sso_id || '',
      action_code: 'g01dos',
      tax_year: this.selectedTaxYear,
      scenario: this.selectedScenario,
      jcd_key: this.jcd_key,
      GROUP_KEY: GROUP_KEY,
      IS_ENABLED: IS_ENABLED ? 'Y' : 'N'
    };

    this.autoTagProcessService.updateTransJob(
      this.GENERAL_CONFIG.base_url,
      params
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      () => {
        this.savingGroupKey = null;
        this.cdr.detectChanges();
        this.getTransType();
      },
      (error: any) => {
        console.error('Error updating trans job:', error);
        this.savingGroupKey = null;
        this.cdr.detectChanges();
        this.gtwSnackbarService.openSnackBar('Error updating transaction type.', 'gtw-snackbar--red');
      }
    );
  }

  getFilingGroup(): void {
    if (!this.selectedScenario || !this.selectedTaxYear) {
      return;
    }

    const params = {
      action_code: 'z60z07',
      scenario: this.selectedScenario,
      tax_year: this.selectedTaxYear
    };

    const globalServiceWrapper = { globalParams: this.globalService };

    this.autoTagProcessService.loadJsonObject(
      this.GENERAL_CONFIG.base_url,
      params,
      globalServiceWrapper
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.filingGroupDropDown = data?.jsonObject || [];
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error('Error loading filing group:', error);
        this.filingGroupDropDown = [];
        this.cdr.markForCheck();
      }
    );
  }

  getFilingEnityDetails(): void {
    if (!this.filingGroup || !this.selectedScenario || !this.selectedTaxYear) {
      return;
    }

    const parentMeKeys = this.parentME?.length > 0
      ? _.map(this.parentME, 'PARENT_ME_KEY').join()
      : '';

    const params = {
      action_code: '35gc5l',
      filing_group_key: this.filingGroup,
      scenario: this.selectedScenario,
      tax_year: this.selectedTaxYear,
      parent_me_keys: parentMeKeys
    };

    const globalServiceWrapper = { globalParams: this.globalService };

    this.autoTagProcessService.loadJsonObject(
      this.GENERAL_CONFIG.base_url,
      params,
      globalServiceWrapper
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.filerEntity = [];
        this.filingEnityDetails = data?.jsonObject || [];
        this.syncDropdownList('filerLE', this.filingEnityDetails);
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error('Error loading filing entity details:', error);
        this.filingEnityDetails = [];
        this.filerEntity = [];
        this.syncDropdownList('filerLE', this.filingEnityDetails);
        this.cdr.markForCheck();
      }
    );
  }

  getParentMeDetails(): void {
    const params = { action_code: 'hmkia6' };

    const globalServiceWrapper = { globalParams: this.globalService };

    this.autoTagProcessService.loadJsonObject(
      this.GENERAL_CONFIG.base_url,
      params,
      globalServiceWrapper
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data?.jsonObject) {
          this.parentDetails = data.jsonObject.filter((d: any) => d.LEVEL_GROUP === 'Parent ME');
          this.syncDropdownList('parentME', this.parentDetails);
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error('Error loading parent ME details:', error);
        this.parentDetails = [];
        this.syncDropdownList('parentME', this.parentDetails);
        this.cdr.markForCheck();
      }
    );
  }

  onAutoTagClick(): void {
    if (!this.selectedTaxYear) {
      this.gtwSnackbarService.openSnackBar('Please select a valid Tax Year.', 'gtw-snackbar--red');
      return;
    }
    if (!this.selectedScenario) {
      this.gtwSnackbarService.openSnackBar('Please select a valid Scenario.', 'gtw-snackbar--red');
      return;
    }
    if (!this.parentME?.length) {
      this.gtwSnackbarService.openSnackBar('Please select the valid ME(s).', 'gtw-snackbar--red');
      return;
    }
    if (!this.filingGroup) {
      this.gtwSnackbarService.openSnackBar('Please select a valid Filing Group.', 'gtw-snackbar--red');
      return;
    }
    if (!this.filerEntity?.length) {
      this.gtwSnackbarService.openSnackBar('Please select a valid Filing Entity.', 'gtw-snackbar--red');
      return;
    }
    this.showConfirmModal = true;
    this.cdr.detectChanges();
  }

  onConfirmAutoTag(): void {
    this.showConfirmModal = false;
    this.cdr.detectChanges();
    this.autoTagProcess();
  }

  onCancelConfirm(): void {
    this.showConfirmModal = false;
    this.cdr.detectChanges();
  }

  autoTagProcess(): void {
    const checkLockedbj = this.parentME.map((_item: any, indx: number) => ({
      me_code: _.map(this.parentME, 'PARENT_ME_CODE')[indx],
      consol_group_key: this.filingGroup,
      combination_key: _.map(this.filerEntity, 'FILER_COMBINATION_KEY').join()
    }));

    const params = {
      sso_id: this.USER_SETTINGS?.user?.sso_id || '',
      action_code: 'zfwjr9',
      tax_year: this.selectedTaxYear,
      scenario: this.selectedScenario,
      jcd_key: this.jcd_key,
      consol_group_key: this.filingGroup,
      FILER_LE_KEY: _.map(this.filerEntity, 'FILER_LE_KEY').join(),
      me_key: _.map(this.parentME, 'PARENT_ME_KEY').join(),
      issue_id: this.globalService?.issue_id || '',
      issue_short_desc: this.globalService?.issue_short_desc || '',
      checkLockedbj: JSON.stringify(checkLockedbj)
    };

    this.autoTagProcessService.saveAutoTag(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        if (result?.callSuccess != 1) {
          this.gtwSnackbarService.openSnackBar(result?.errorMessage || 'Error', 'gtw-snackbar--red');
          return;
        }
        this.gtwSnackbarService.openSnackBar(
          'Process to Create a Tag is initiated. Email will be sent, after the process is complete.',
          'gtw-snackbar--green'
        );
        this.requestGridUpdate.emit('refresh');
      },
      (error: any) => {
        console.error('Error initiating auto tag process:', error);
        this.gtwSnackbarService.openSnackBar('Error initiating auto tag process. Please try again.', 'gtw-snackbar--red');
      }
    );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  onDropdownFilterResult(key: string, event: any): void {
    this.dropdownLists[key] = event?.result || [];
  }

  private toAppkitOptions(items: any[]): any[] {
    return (items || []).map((item: any) => {
      item.showCheckbox = true;
      item.role = 'checkbox';
      return item;
    });
  }

  private syncDropdownList(key: string, master: any[]): void {
    this.dropdownLists[key] = this.toAppkitOptions(master).slice();
  }
}
